<script setup>

import {computed, reactive} from "vue"

import InputRadio from "@shared/components/atoms/form/InputRadio.vue"
import AlertModal from "@shared/components/modals/AlertModal.vue"
import useHelpers from "@shared/composables/useHelpers"
import {useNotificationsStore} from "@shared/stores/NotificationsStore"
import {useTransactionsStore} from "@shared/stores/TransactionsStore"

const state = reactive({
    type: 'all',
    isConfirmReverseTransactionModalVisible: false,
    transactionIdToReverse: null
})

const {formatDate} = useHelpers()

const notificationStore = useNotificationsStore()
const transactionsStore = useTransactionsStore()

const filteredTransactions = computed(() =>
    transactionsStore.getTransactionsByType(state.type)
)

const handleReverseTransactionClick = (transactionId) => {
    state.transactionIdToReverse = transactionId
    state.isConfirmReverseTransactionModalVisible = true
}

const cancelReverseTransaction = () => {
    state.transactionIdToReverse = null
    state.isConfirmReverseTransactionModalVisible = false
}

const confirmReverseTransaction = async () => {
    try {
        const { withdrawCount } = await transactionsStore.reverseTransaction(state.transactionIdToReverse)
        const messages = [
            "Your withdrawal was successfully reversed. Please note, you can only reverse 2 more withdrawals today.",
            "Your withdrawal was successfully reversed. Please note, you only have 1 more withdrawal reverse remaining today.",
            "Your withdrawal was successfully reversed. Please note, you can only queue withdrawals again after midnight."
        ]
        notificationStore.showAlert(messages[withdrawCount - 1] || messages[2])
    } catch (e) {
        console.error({e})
    } finally {
        state.transactionIdToReverse = null
        state.isConfirmReverseTransactionModalVisible = false
    }
}
</script>

<template>
    <AlertModal
        v-if="state.isConfirmReverseTransactionModalVisible"
        @click:cancel="cancelReverseTransaction"
        @click:confirm="confirmReverseTransaction"
        confirmText="YES"
        cancelText="NO"
    >
        <template v-if="transactionsStore.dailyWthdrawCount === 3">
            This is your last reversal for today. Are you sure you would like to reverse your queued withdrawal with transaction ID <span style='color:#0cd35d'>{{ state.transactionIdToReverse }}</span>? Your next withdrawal can only be queued after midnight.
        </template>
        <template v-else>
            Are you sure you would like to reverse your queued withdraw with transaction ID <span style='color:#0cd35d'>{{ state.transactionIdToReverse }}</span>? Please note, you are allowed to reverse up to 3 withdrawals per day.
        </template>
    </AlertModal>
    <div
        v-if="transactionsStore.isTransactionModalVisible"
        id="divAppTransactionHistory"
        class="show"
    >
        <div class="divTransactionHistoryOverlay"></div>
        <div class="divTransactionHistoryPopup">
            <div class="divTransactionHistoryHeading">Transaction History</div>
            <div
                id="btnCloseTransactionHistory"
                @click="transactionsStore.setTransactionModalVisibility(false)"
            />
            <div class="divTransactionColumnHeadings">
                <div>Transaction</div>
                <div>Amount</div>
            </div>

            <div id="divHistoryTransactions">

                <div
                    v-for="transaction in filteredTransactions"
                    :class="{
                        divTransaction: true,
                        failed: transaction.status === 'failed'
                    }"
                    :key="`transaction-${transaction.id}`"
                >
                    <div class="divTransactionDetails">
                        <div class="divTransactionDateId">
                            <span>{{ formatDate(transaction.created_at) }}</span> - {{ transaction.transactionId }}
                        </div>
                        <div class="divTransactionStatus">
                            {{ transaction.typeString }}
                            <div
                                v-if="transaction.status === 'queued'"
                                @click="handleReverseTransactionClick(transaction.transactionId)"
                                class="divCancelWithdraw"
                            >REVERSE</div>
                        </div>
                    </div>
                    <div class="divTransactionAmount">{{ transaction.amount }}</div>
                </div>
            </div>

            <div class="divTransactionTypes">
                <InputRadio
                    v-model="state.type"
                    :options="{
                        all: 'All',
                        deposit: 'Deposit',
                        allocation: 'Allocation',
                        withdraw: 'Withdraw',
                        bonus: 'Bonus',
                        win: 'Win',
                    }"
                />
            </div>
        </div>
    </div>
</template>
